/**
 * Authentication Provider
 *
 * Subscribes to Supabase auth state changes.
 * On INITIAL_SESSION, fetches user/tenant/env context from backend.
 * Blocks children until auth state is resolved (isLoading: true by default).
 */

import { type ReactNode, useEffect, useRef } from 'react'
import { authService } from '@/features/auth/services/authService'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { resetLogoutGuard } from '@/lib/api-client'
import { supabase } from '@/shared/lib/supabase/client'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isLoading } = useAuthStore()
  const hasInitialized = useRef(false)

  useEffect(() => {
    // Clean up orphaned legacy token keys
    localStorage.removeItem('coobird_auth_token')
    localStorage.removeItem('coobird_refresh_token')

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip during OAuth callback — the callback page handles its own sync
      if (window.location.pathname === '/auth/callback') {
        return
      }

      if (event === 'INITIAL_SESSION') {
        if (session && !hasInitialized.current) {
          hasInitialized.current = true
          try {
            // Proactively refresh session to ensure fresh tokens.
            // getSession() returns cached (potentially expired) tokens from localStorage.
            // refreshSession() fetches fresh tokens from Supabase GoTrue.
            // Supabase JS doesn't throw on error — must check return value explicitly.
            const { error: refreshError } = await supabase.auth.refreshSession()
            if (refreshError) {
              throw refreshError
            }

            const response = await authService.getCurrentUser()

            // Reset logout guard on successful auth
            resetLogoutGuard()

            useAuthStore.setState({
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              hasInitialized: true,
            })

            // Store tenant/environment context — only if no user selection exists
            // User's explicit switch (stored in localStorage) takes precedence over backend defaults
            if (
              response.tenant?.id &&
              !localStorage.getItem('current_tenant_id')
            ) {
              localStorage.setItem('current_tenant_id', response.tenant.id)
            }
            if (
              response.environment?.id &&
              !localStorage.getItem('current_environment_id')
            ) {
              localStorage.setItem(
                'current_environment_id',
                response.environment.id
              )
            }
          } catch {
            // Session restoration failed — clear auth state gracefully
            await supabase.auth.signOut().catch(() => {})
            localStorage.removeItem('current_tenant_id')
            localStorage.removeItem('current_environment_id')
            useAuthStore.setState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              hasInitialized: true,
            })
          }
        } else if (!session) {
          // No session — not authenticated
          useAuthStore.setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasInitialized: true,
          })
        }
      }

      if (event === 'SIGNED_OUT') {
        useAuthStore.setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })
      }

      // TOKEN_REFRESHED — no action needed.
      // supabase.auth.getSession() returns fresh tokens automatically.
    })

    return () => subscription.unsubscribe()
  }, [])

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100' />
      </div>
    )
  }

  return <>{children}</>
}
